import type { ReactNode } from 'react';
import { Icon } from '@/core/ui';
import type { PresentWeather } from '../model/types';

// One icon per station detail, on the shared 24px grid (see core/ui Icon), small and light next to text.
// Each has its own muted colour, tied to what it measures, so the rows are told apart at a glance.

function DetailIcon({ color, children }: { color: string; children: ReactNode }) {
  return (
    <Icon className={`size-3.5 ${color}`} strokeWidth={1.75}>
      {children}
    </Icon>
  );
}

export function WindIcon() {
  return (
    <DetailIcon color="text-sky-500">
      <path d="M2 9h11a3 3 0 1 0-3-3M2 13h16a3 3 0 1 1-3 3M2 17h6" />
    </DetailIcon>
  );
}

/** A wind streak above a sudden spike. */
export function GustIcon() {
  return (
    <DetailIcon color="text-violet-400">
      <path d="M2 7h10a3 3 0 1 0-3-3M2 16h4l2.5-4 3 8 2.5-4h8" />
    </DetailIcon>
  );
}

export function TemperatureIcon() {
  return (
    <DetailIcon color="text-rose-400">
      <path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0zM12 10v7" />
    </DetailIcon>
  );
}

/** Terrain with an arrow up: the height the air "feels" like. */
export function DensityAltitudeIcon() {
  return (
    <DetailIcon color="text-amber-500">
      <path d="M2 21 8 12l3.5 4.5L14 14l5 7zM18 10V2M15 5l3-3 3 3" />
    </DetailIcon>
  );
}

export function ElevationIcon() {
  return (
    <DetailIcon color="text-emerald-600">
      <path d="M2 21 9 7l4.5 7.5L16 11l6 10z" />
    </DetailIcon>
  );
}

export function LocationIcon() {
  return (
    <DetailIcon color="text-neutral-400">
      <path d="M12 22s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12z" />
      <circle cx="12" cy="10" r="2.5" />
    </DetailIcon>
  );
}

const CLOUD = 'M7 14a4 4 0 0 1-.6-7.95A6 6 0 0 1 17.7 7.2 3.5 3.5 0 0 1 17 14z';

/** A cloud with what falls from it: rain streaks, snow dots, hail stones, or a lightning bolt. */
export function PrecipitationIcon({ weather }: { weather: PresentWeather | null }) {
  const kinds = new Set(weather?.precipitation.map((p) => p.kind));
  const below = weather?.thunder ? (
    <path d="M13 16l-3 3.5h4L11 23" />
  ) : kinds.has('hail') || kinds.has('icePellets') ? (
    <>
      <circle cx="8" cy="19" r="1.2" />
      <circle cx="12" cy="21.5" r="1.2" />
      <circle cx="16" cy="19" r="1.2" />
    </>
  ) : kinds.has('snow') ? (
    <path d="M8 18h.01M12 21h.01M16 18h.01M12 17h.01M8 22h.01M16 22h.01" />
  ) : (
    <path d="M8 17.5l-1 3M12 17.5l-1 3M16 17.5l-1 3" />
  );
  return (
    <DetailIcon color="text-blue-500">
      <path d={CLOUD} />
      {below}
    </DetailIcon>
  );
}
